/**
 * Managed Years hook.
 *
 * Returns the merged list of (a) years with data + (b) admin-added
 * "managed" years from the backend, plus mutation helpers for the "+
 * Add Year" button on every Year picker across the app.
 *
 * Why hook (not Context): the list changes rarely and the cost of a
 * refetch per picker instance is negligible — fewer moving parts than
 * threading another Provider through App.js.
 */
import { useCallback, useEffect, useState } from 'react';
import { authFetch, authJsonFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const ADMIN_EMAILS = new Set(['j.win@gsimaging.com.au']);

export const useManagedYears = () => {
  const { user } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || ADMIN_EMAILS.has(user.email));

  const currentYear = new Date().getFullYear();
  // Sensible fallback until the API responds — covers the typical ops
  // range and keeps pickers from rendering empty on first paint.
  const [years, setYears] = useState([currentYear - 2, currentYear - 1, currentYear, currentYear + 1]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/years/managed`);
      if (!res.ok) return;
      const j = await res.json();
      const list = Array.isArray(j?.years) ? j.years.map(Number).filter(Number.isFinite) : [];
      if (list.length) setYears(list.sort((a, b) => a - b));
    } catch (e) {
      // Silent — fallback years remain.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const addYear = useCallback(async (year) => {
    if (!isAdmin) {
      toast.error('Only admins can add new years');
      return false;
    }
    const n = Number(year);
    if (!Number.isFinite(n) || n < 2000 || n > 2100) {
      toast.error('Please enter a year between 2000 and 2100');
      return false;
    }
    try {
      const res = await authJsonFetch(`${API}/years/managed`, {
        method: 'POST',
        body: JSON.stringify({ year: n }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not add year');
        return false;
      }
      await reload();
      toast.success(`Added ${n}`);
      return true;
    } catch (e) {
      toast.error('Could not add year');
      return false;
    }
  }, [isAdmin, reload]);

  return { years, loading, reload, addYear, isAdmin };
};
